import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useProgress } from '@/hooks/useProgress';
import SegmentCard from '@/components/SegmentCard';
import logomark from '@/assets/goodera-logomark.png';
import { HOME } from '@/constants/testIds';

const SEGMENTS = [
  {
    id: 1,
    title: 'Base Camp',
    description: 'Welcome, why we\'re here, and who\'s in the room.',
    duration: '~13 min',
  },
  {
    id: 2,
    title: 'Trail Markers',
    description: 'Two boards, two questions, one room getting to know itself.',
    duration: '10 min',
  },
  {
    id: 3,
    title: 'The Briefing',
    description: 'Where AI is in India\'s social sector, one cautionary tale, one framework.',
    duration: '10 min',
  },
  {
    id: 4,
    title: 'Gear Check',
    description: 'Two tabs open, workspace live, ready to build.',
    duration: '8 min',
  },
  {
    id: 5,
    title: 'Prompt School',
    description: 'Learn R-T-C-F, then draft your organization\'s AI policy.',
    duration: '34 min',
  },
  {
    id: 6,
    title: 'The Build',
    description: 'Pick a recipe, build something real, keep it forever.',
    duration: '46 min',
  },
  {
    id: 7,
    title: 'The Exchange',
    description: 'Post one prompt. Stock the community library.',
    duration: '6 min',
  },
  {
    id: 8,
    title: 'Show & Tell',
    description: 'Six organizations, six builds, fifteen minutes.',
    duration: '15 min',
  },
  {
    id: 9,
    title: 'The Sustainable Stack',
    description: 'What you keep, what it costs (almost nothing), what\'s next.',
    duration: '6 min',
  },
];

const MetaPill = ({ children }) => (
  <span className="inline-flex px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-medium">
    {children}
  </span>
);

const HomePage = () => {
  const navigate = useNavigate();
  const { completedCount, progress, isCompleted, nextSegment } = useProgress();

  return (
    <div className="py-10 md:py-14">
      <div className="max-w-6xl mx-auto px-6">

        {/* ── HERO CARD ── */}
        <div
          data-testid={HOME.heroCard}
          className="relative bg-ink overflow-hidden p-8 md:p-10 lg:p-12"
          style={{ borderRadius: '1.5rem 1.5rem 4rem 1.5rem' }}
        >
          {/* Goodera logomark accent — anchored to right edge, vertically centered, right half clipped */}
          <img
            src={logomark}
            alt=""
            aria-hidden="true"
            data-testid="hero-logomark"
            className="pointer-events-none select-none absolute top-1/2 right-6 -translate-y-1/2 h-[70%] w-auto opacity-50"
          />

          {/* Content — sits above the SVG */}
          <div className="relative z-10 max-w-2xl">
            {/* LIVE SESSION chip */}
            <div
              data-testid={HOME.liveChip}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 mb-5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />
              <span className="text-white text-xs font-semibold tracking-widest uppercase">
                Live Session
              </span>
            </div>

            {/* Title */}
            <h1
              data-testid={HOME.heroTitle}
              className="text-white font-bold text-4xl md:text-5xl leading-tight mb-3"
            >
              The AI Lab
            </h1>

            {/* Subtitle */}
            <p
              data-testid={HOME.heroSubtitle}
              className="text-white/60 text-base md:text-lg mb-6"
            >
              Builders coaching Bengaluru&rsquo;s nonprofits &mdash; 12 August &middot; 11 AM–2 PM
            </p>

            {/* Metadata pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              <MetaPill>Goodera × Basecamp</MetaPill>
              <MetaPill>9 segments</MetaPill>
              <MetaPill>Bengaluru</MetaPill>
            </div>

            {/* CTA + Progress bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                data-testid={HOME.heroEnterButton}
                onClick={() => navigate(`/session/${nextSegment}`)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white rounded-full font-semibold text-sm hover:bg-brand-orange-hover transition-colors shrink-0"
              >
                Enter the session
                <ArrowRight size={16} strokeWidth={2.5} />
              </button>

              <div className="flex flex-1 items-center gap-3 w-full sm:w-auto">
                <div
                  data-testid={HOME.heroProgressBar}
                  className="flex-1 h-1.5 bg-white/15 rounded-full overflow-hidden min-w-[120px]"
                >
                  <div
                    data-testid={HOME.heroProgressFill}
                    className="h-full bg-brand-orange rounded-full transition-all duration-700"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-white/50 text-xs font-medium shrink-0 tabular-nums">
                  {completedCount}/9
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── CONTENTS SECTION ── */}
        <section data-testid={HOME.contentsSection} className="mt-14">
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="text-xl font-bold text-ink">Contents</h2>
            <div className="h-px flex-1 bg-black/[0.07]" />
          </div>

          {/* 9-segment grid */}
          <div
            data-testid={HOME.contentsGrid}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {SEGMENTS.map((segment) => (
              <SegmentCard
                key={segment.id}
                segment={segment}
                isCompleted={isCompleted(segment.id)}
              />
            ))}
          </div>
        </section>

        {/* ── LINK CARDS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Exchange link */}
          <Link
            to="/exchange"
            data-testid={HOME.exchangeLinkCard}
            className="group block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-ink-muted mb-1">
                  Community
                </p>
                <h3 className="text-lg font-bold text-ink group-hover:text-brand-orange transition-colors">
                  Prompt & Skills Exchange
                </h3>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-orange flex items-center justify-center shrink-0 group-hover:bg-brand-orange-hover transition-colors ml-4">
                <ArrowRight size={18} className="text-white" strokeWidth={2} />
              </div>
            </div>
          </Link>

          {/* Mentors link */}
          <Link
            to="/mentors"
            data-testid={HOME.mentorsLinkCard}
            className="group block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-ink-muted mb-1">
                  Resources
                </p>
                <h3 className="text-lg font-bold text-ink group-hover:text-brand-orange transition-colors">
                  Mentor Resources
                </h3>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-orange flex items-center justify-center shrink-0 group-hover:bg-brand-orange-hover transition-colors ml-4">
                <ArrowRight size={18} className="text-white" strokeWidth={2} />
              </div>
            </div>
          </Link>
        </div>

      </div>
    </div>
  );
};

export default HomePage;
